import * as THREE from 'three';

import { FileType } from '../../types/file';
import { Mesh } from '../../types/mesh';
import { NewProjectRequest } from '../../types/project';
import { UnitSystem } from '../../types/util';
import { inferContentType } from '../../utils/contentTypeUtils';
import { mapModelLayersToSettings } from '../../utils/layerUtils';
import { cloneParsedModel, loadParsedModel } from '../../utils/modelLoadCache';
import { rescaleModelBlob } from '../../utils/rescaleModel';

export type ModelUnit = 'm' | 'cm' | 'mm' | 'ft' | 'in';

export const UNIT_METERS: Record<ModelUnit, number> = { m: 1, cm: 0.01, mm: 0.001, ft: 0.3048, in: 0.0254 };

/** A model that cannot be used as it is; `message` is a translation key. */
export class ModelUploadError extends Error {}

export interface ModelAnalysis {
  /** Layer (mesh) names as the workspace reads them, in model order. */
  layerNames: string[];
  /** The unit that makes the site a plausible size (30 m to 30 km across). */
  unit: ModelUnit;
  /** Top-down-ish picture of the model, base64 PNG without the data: prefix. */
  thumbnail: string;
}

const extension = (file: File) => file.name.split('.').pop()?.toLowerCase() ?? '';

export const isModelFile = (file: File) => ['glb', 'obj'].includes(extension(file));

/**
 * The unit of a model: metres when that is plausible, else millimetres (the usual CAD/BIM export),
 * else centimetres, whichever first makes the site 30 m to 30 km across. The user can change it.
 */
export function guessModelUnit(sizeAcross: number): ModelUnit {
  const plausible = (unit: ModelUnit) => {
    const metres = sizeAcross * UNIT_METERS[unit];
    return metres >= 30 && metres <= 30000;
  };
  return (['m', 'mm', 'cm'] as ModelUnit[]).find(plausible) ?? 'm';
}

function renderThumbnail(object: THREE.Object3D, box: THREE.Box3): string {
  const width = 640;
  const height = 400;
  // Transparent: the picture sits on the card or dialog behind it.
  const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: true });
  try {
    renderer.setSize(width, height, false);
    renderer.setClearColor(0x000000, 0);
    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 2.2));
    const sun = new THREE.DirectionalLight(0xffffff, 1.5);
    sun.position.set(1, 2, 1);
    scene.add(sun);
    scene.add(object);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z) || 1;
    const camera = new THREE.PerspectiveCamera(40, width / height, radius / 100, radius * 20);
    // Models are Y-up: look down on the site at an angle, close enough for it to fill the picture.
    const direction = new THREE.Vector3(0.6, 1.1, 0.9).normalize();
    const corners = [0, 1, 2, 3, 4, 5, 6, 7].map(
      (i) =>
        new THREE.Vector3(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z),
    );
    let distance = radius * 1.5;
    for (let i = 0; i < 4; i++) {
      camera.position.copy(center).addScaledVector(direction, distance);
      camera.lookAt(center);
      camera.updateMatrixWorld();
      const extent = Math.max(
        ...corners.map((corner) => {
          const p = corner.clone().project(camera);
          return Math.max(Math.abs(p.x), Math.abs(p.y));
        }),
      );
      distance *= extent / 0.9;
    }
    renderer.render(scene, camera);
    const dataUrl = renderer.domElement.toDataURL('image/png');
    return dataUrl.slice(dataUrl.indexOf(',') + 1);
  } finally {
    renderer.dispose();
    renderer.forceContextLoss();
  }
}

/** Reads uploaded models the way the workspace will: layer names, a unit guess and a thumbnail. */
export async function analyzeModels(files: File[]): Promise<ModelAnalysis> {
  const layerNames: string[] = [];
  const group = new THREE.Group();
  for (const file of files) {
    const url = URL.createObjectURL(file);
    try {
      const { object, originalNames } = cloneParsedModel(await loadParsedModel(url, extension(file)));
      // Same naming as the workspace (UploadedModel): the n-th mesh takes the n-th node name.
      let counter = 0;
      object.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          layerNames.push(originalNames.get(counter) ?? (child.name || `Mesh_${counter}`));
          counter++;
        }
      });
      group.add(object);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  if (layerNames.length === 0) throw new ModelUploadError('projects.quickCreate.noLayers');
  if (new Set(layerNames).size !== layerNames.length)
    throw new ModelUploadError('projects.quickCreate.duplicateLayers');
  const box = new THREE.Box3().setFromObject(group);
  const size = box.getSize(new THREE.Vector3());
  return { layerNames, unit: guessModelUnit(Math.hypot(size.x, size.z)), thumbnail: renderThumbnail(group, box) };
}

/** Unlit layers with a type guessed from their names (as the old wizard did). */
function layersToMeshes(names: string[], modelName: string): Mesh[] {
  const layers = names.map((name) => ({
    uuid: crypto.randomUUID(),
    originalName: name,
    threeJsName: name,
    displayName: name,
    modelName,
    visible: true,
  }));
  return mapModelLayersToSettings(layers).map((layer) => ({
    uuid: layer.uuid,
    name: layer.originalName,
    displayName: layer.displayName,
    color: layer.color,
    type: layer.type.toUpperCase(),
    canHoldLight: false,
    isIlluminated: false,
    desiredLuminaireHeight: layer.desiredLuminaireHeight,
  }));
}

interface ModelProjectInput {
  name: string;
  files: File[];
  unit: ModelUnit;
  analysis: ModelAnalysis;
  settings: { tolerance?: number; maintenance?: number; units?: UnitSystem } | undefined;
  folderId?: number;
}

/**
 * The create-project request for uploaded models: files scaled to metres, every layer unlit (lighting
 * is set up in the workspace) and the thumbnail as preview.
 */
export async function buildModelProjectForm(input: ModelProjectInput): Promise<FormData> {
  const scale = UNIT_METERS[input.unit];
  const models = await Promise.all(
    input.files.map(async (file) => {
      // The server wants the model's own type; browsers often leave it empty (OBJ, fetched files).
      const type = file.type && file.type !== 'application/octet-stream' ? file.type : inferContentType(file.name);
      if (scale === 1) return new File([file], file.name, { type });
      const url = URL.createObjectURL(file);
      try {
        return new File([await rescaleModelBlob(url, file.name, scale)], file.name, { type });
      } finally {
        URL.revokeObjectURL(url);
      }
    }),
  );
  const request: NewProjectRequest = {
    name: input.name,
    description: '',
    tolerance: input.settings?.tolerance ?? 0,
    maintenance: input.settings?.maintenance ?? 0,
    units: input.settings?.units ?? UnitSystem.METRIC,
    latitude: 0,
    longitude: 0,
    previewImageBase64: input.analysis.thumbnail,
    props: models.map((file) => ({ name: file.name, fileType: FileType.OBJECT_3D })),
    lights: [],
    meshes: layersToMeshes(input.analysis.layerNames, input.files[0]?.name ?? ''),
    meshGroups: [],
    folderId: input.folderId,
  };
  const form = new FormData();
  form.append('project', new Blob([JSON.stringify(request)], { type: 'application/json' }));
  models.forEach((file) => form.append('models', file));
  return form;
}
